import { RequestLeaveAction } from "__generated__/graphql"
import { useMutation } from "@apollo/client"
import {
  Logout as LogoutIcon,
  RocketLaunch as RocketLaunchIcon,
  SettingsRemote as SettingsRemoteIcon,
  WatchLater as WatchLaterIcon,
} from "@mui/icons-material"
import { LoadingButton } from "@mui/lab"
import { Box, Typography } from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import Stack from "@mui/material/Stack"
import TextField from "@mui/material/TextField"
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker"
import { AttendeeAutocomplete } from "components/AgencySelect"
import { DialogCloseButton, Flex, FlexColumn, SurfacePaper } from "components/common"
import { DateTime } from "luxon"
import { useSnackbar } from "notistack"
import { useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useSelector } from "react-redux"
import { profileSelector } from "reducers/profileSlice"
import { REQUEST_ACTION_LABELS } from "views/RequestLeave/constants"
import { ADD_REQUEST_LEAVE, FIND_REQUEST_LEAVES } from "views/RequestLeave/graphql"
import { REQUEST_LEAVE_LAYOUT } from "views/RequestLeave/styles"

type FormValues = {
  approver: Attendee | null
  endDate: DateTime | null
  note: string
  startDate: DateTime | null
}

const RequestListForm = () => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogType, setDialogType] = useState<null | RequestLeaveAction>(null)
  const { enqueueSnackbar } = useSnackbar()
  const { user } = useSelector(profileSelector)
  const [addRequestLeave, { loading }] = useMutation(ADD_REQUEST_LEAVE)

  const {
    control,
    formState: { isSubmitting },
    getValues,
    handleSubmit,
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      approver: null,
      endDate: null,
      note: "",
      startDate: null,
    },
  })

  const openDialog = (type: RequestLeaveAction) => {
    setDialogType(type)
    setDialogOpen(true)
  }

  const closeDialog = () => {
    setDialogOpen(false)
    setDialogType(null)
    reset()
  }

  const onSubmit = async (values: FormValues) => {
    const attendeeDiscordId = user?.attendee?.discordId
    const approverDiscordId = values.approver?.discordId
    const startDate = values.startDate?.toISO()
    const endDate = values.endDate?.toISO()

    if (!attendeeDiscordId || !approverDiscordId || !dialogType || !startDate || !endDate) {
      enqueueSnackbar("Thông tin yêu cầu chưa đầy đủ.", { variant: "error" })
      return
    }

    try {
      await addRequestLeave({
        refetchQueries: [FIND_REQUEST_LEAVES],
        variables: {
          action: dialogType,
          approverDiscordId,
          attendeeDiscordId,
          endDate,
          reason: values.note.trim(),
          startDate,
        },
      })
      enqueueSnackbar("Đã lưu thành công!", { variant: "success" })
      closeDialog()
    } catch {
      enqueueSnackbar("Không thể gửi yêu cầu. Vui lòng thử lại.", { variant: "error" })
    }
  }

  return (
    <FlexColumn>
      <SurfacePaper sx={{ p: 1.5 }} variant="outlined">
        <Flex alignItems="center" className="flex-wrap" gap={1.5}>
          <Box minWidth={140}>
            <Typography fontWeight={700} variant="subtitle2">
              Tạo yêu cầu
            </Typography>
            <Typography color="text.secondary" variant="caption">
              Chọn loại phù hợp
            </Typography>
          </Box>
          <Flex className="flex-wrap" flex={1} gap={1}>
            <Button
              onClick={() => openDialog(RequestLeaveAction.RequestLeave)}
              startIcon={<LogoutIcon />}
              variant="contained"
            >
              Xin nghỉ phép
            </Button>
            <Button
              onClick={() => openDialog(RequestLeaveAction.RequestLate)}
              startIcon={<WatchLaterIcon />}
              variant="outlined"
            >
              Xin đi muộn
            </Button>
            <Button
              onClick={() => openDialog(RequestLeaveAction.RequestLeaveEarly)}
              startIcon={<RocketLaunchIcon />}
              variant="outlined"
            >
              Xin về sớm
            </Button>
            <Button
              onClick={() => openDialog(RequestLeaveAction.RequestRemote)}
              startIcon={<SettingsRemoteIcon />}
              variant="outlined"
            >
              Xin remote
            </Button>
          </Flex>
        </Flex>
      </SurfacePaper>
      <Dialog fullWidth maxWidth="sm" onClose={closeDialog} open={dialogOpen}>
        <DialogTitle>
          <Typography fontWeight={700} variant="h6">
            {dialogType ? REQUEST_ACTION_LABELS[dialogType] : "Tạo yêu cầu"}
          </Typography>
          <Typography color="text.secondary" variant="caption">
            Điền đầy đủ thông tin để gửi người xét duyệt.
          </Typography>
          <DialogCloseButton aria-label="Đóng" onClick={closeDialog} />
        </DialogTitle>
        <DialogContent>
          <Stack gap={2.5} pt={0.5}>
            <Flex className="flex-wrap" gap={2}>
              <Controller
                control={control}
                name="startDate"
                render={({ field, fieldState: { error } }) => (
                  <DateTimePicker
                    label="Ngày bắt đầu"
                    onChange={field.onChange}
                    slotProps={{
                      textField: {
                        error: Boolean(error),
                        fullWidth: true,
                        helperText: error?.message,
                        required: true,
                        sx: { flex: "1 1 220px" },
                      },
                    }}
                    value={field.value}
                  />
                )}
                rules={{
                  validate: (value) => Boolean(value?.isValid) || "Vui lòng chọn ngày bắt đầu",
                }}
              />
              <Controller
                control={control}
                name="endDate"
                render={({ field, fieldState: { error } }) => (
                  <DateTimePicker
                    label="Ngày kết thúc"
                    onChange={field.onChange}
                    slotProps={{
                      textField: {
                        error: Boolean(error),
                        fullWidth: true,
                        helperText: error?.message,
                        required: true,
                        sx: { flex: "1 1 220px" },
                      },
                    }}
                    value={field.value}
                  />
                )}
                rules={{
                  validate: (value) => {
                    if (!value?.isValid) return "Vui lòng chọn ngày kết thúc"
                    const startDate = getValues("startDate")
                    return (
                      !startDate?.isValid ||
                      value.toMillis() >= startDate.toMillis() ||
                      "Ngày kết thúc phải sau ngày bắt đầu"
                    )
                  },
                }}
              />
            </Flex>
            <Controller
              control={control}
              name="note"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  fullWidth
                  label="Lý do nghỉ phép"
                  minRows={3}
                  multiline
                  required
                  {...field}
                  error={Boolean(error)}
                  helperText={error?.message}
                />
              )}
              rules={{
                validate: (value) => value.trim().length > 0 || "Vui lòng nhập lý do nghỉ phép",
              }}
            />
            <Controller
              control={control}
              name="approver"
              render={({ field, fieldState: { error } }) => (
                <AttendeeAutocomplete
                  error={Boolean(error)}
                  excludeAttendeeIds={user?.attendee ? [user.attendee.id] : []}
                  helperText={error?.message}
                  inputRef={field.ref}
                  label="Người xét duyệt"
                  loadErrorText="Không thể tải danh sách người xét duyệt"
                  noOptionsText="Không có người xét duyệt phù hợp"
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  required
                  value={field.value}
                />
              )}
              rules={{
                validate: (value) => Boolean(value) || "Vui lòng chọn người xét duyệt",
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={loading}
            onClick={closeDialog}
            sx={{ minWidth: REQUEST_LEAVE_LAYOUT.dialogActionMinWidth }}
            variant="outlined"
          >
            Hủy
          </Button>
          <LoadingButton
            loading={isSubmitting}
            onClick={handleSubmit(onSubmit)}
            sx={{ minWidth: REQUEST_LEAVE_LAYOUT.dialogActionMinWidth }}
            variant="contained"
          >
            Lưu
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </FlexColumn>
  )
}

export default RequestListForm
